import { useMemo, useState, useEffect, useCallback } from 'react';

import axios, { endpoints } from 'src/utils/axios';

import { LOCAL_STORAGE_KEYS } from 'src/config-global';

import { useAuthContext } from '../hooks/use-auth-context';
import { FeatureFlagsContext } from './feature-flags-context';
import type { FeatureFlagKey, FeatureFlagsContextType } from './feature-flags-context';

// ----------------------------------------------------------------------

/** Cache TTL: 60 minutes — flags change rarely */
const CACHE_TTL_MS = 60 * 60 * 1000;

type CacheEntry = {
  flags: Partial<Record<FeatureFlagKey, boolean>>;
  timestamp: number;
  userId: string;
};

function readCache(userId: string): Partial<Record<FeatureFlagKey, boolean>> | null {
  try {
    const raw = localStorage.getItem(LOCAL_STORAGE_KEYS.feature_flags);
    if (!raw) return null;
    const entry: CacheEntry = JSON.parse(raw);
    if (entry.userId !== userId) return null;
    if (Date.now() - entry.timestamp > CACHE_TTL_MS) return null;
    return entry.flags;
  } catch {
    return null;
  }
}

function writeCache(userId: string, flags: Partial<Record<FeatureFlagKey, boolean>>) {
  try {
    const entry: CacheEntry = { flags, timestamp: Date.now(), userId };
    localStorage.setItem(LOCAL_STORAGE_KEYS.feature_flags, JSON.stringify(entry));
  } catch {
    // localStorage unavailable — silently skip
  }
}

/** Normalise the raw API array into a flat flag map */
function normaliseFlagsResponse(
  items: Array<Record<string, unknown>>
): Partial<Record<FeatureFlagKey, boolean>> {
  return items.reduce<Partial<Record<FeatureFlagKey, boolean>>>((acc, item) => {
    // Support both flat { key, enabled } and Strapi nested { attributes: { key, enabled } }
    const attrs = (item.attributes as Record<string, unknown>) ?? item;
    const key = (attrs.key ?? attrs.flag_key) as FeatureFlagKey | undefined;
    const enabled = Boolean(attrs.enabled ?? attrs.enabled_globally ?? attrs.is_enabled ?? true);
    if (!key) return acc;
    return { ...acc, [key]: enabled };
  }, {});
}

// ----------------------------------------------------------------------

type Props = { children: React.ReactNode };

export function FeatureFlagsProvider({ children }: Props) {
  const { authenticated, user } = useAuthContext();
  const userId = (user?.id as string | undefined) ?? '';

  const [flags, setFlags] = useState<Partial<Record<FeatureFlagKey, boolean>>>({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<unknown>(null);

  const fetchFlags = useCallback(async (uid: string) => {
    setLoading(true);
    setError(null);

    try {
      const allItems: Array<Record<string, unknown>> = [];
      let page = 1;

      // eslint-disable-next-line no-constant-condition
      while (true) {
        // eslint-disable-next-line no-await-in-loop
        const response = await axios.get(endpoints.feature_flags, {
          params: { 'pagination[page]': page, 'pagination[pageSize]': 25 },
        });

        const rawItems = Array.isArray(response.data?.data) ? response.data.data : response.data;
        const items: Array<Record<string, unknown>> = Array.isArray(rawItems) ? rawItems : [];
        allItems.push(...items);

        const pagination = response.data?.meta?.pagination;
        if (!pagination || page >= pagination.pageCount) break;
        page += 1;
      }

      const resolved = normaliseFlagsResponse(allItems);
      setFlags(resolved);
      writeCache(uid, resolved);
    } catch (err) {
      setError(err);
      // Fail open: keep existing flags (or empty = all enabled via hook default)
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!authenticated || !userId) {
      setFlags({});
      return;
    }

    // Try cache first
    const cached = readCache(userId);
    if (cached) {
      setFlags(cached);
      return;
    }

    fetchFlags(userId);
  }, [authenticated, userId, fetchFlags]);

  const refresh = useCallback(() => {
    try {
      localStorage.removeItem(LOCAL_STORAGE_KEYS.feature_flags);
    } catch {
      // ignore
    }
    if (userId) fetchFlags(userId);
  }, [userId, fetchFlags]);

  const value = useMemo<FeatureFlagsContextType>(
    () => ({ flags, loading, error, refresh }),
    [flags, loading, error, refresh]
  );

  return <FeatureFlagsContext.Provider value={value}>{children}</FeatureFlagsContext.Provider>;
}
